import { z } from 'zod';

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const IATA = /^[A-Za-z]{3}$/;

export const FlightFilterClockWindowSchema = z.looseObject({
  start: z.string().regex(HHMM).optional(),
  end: z.string().regex(HHMM).optional(),
});

export const FLIGHT_FILTER_PATCH_CABINS = ['economy', 'premium', 'business', 'first'] as const;
export const FlightFilterPatchCabinSchema = z.enum(FLIGHT_FILTER_PATCH_CABINS);
export type FlightFilterPatchCabin = z.infer<typeof FlightFilterPatchCabinSchema>;

/**
 * O PATCH que um chip aplica sobre o estado do painel do explorer (spec
 * flight-text-filter-chips §5.2). Vocabulário do fio de cortes onde ele existe
 * (`ModalSessionFlightCuts`) + os eixos client-side do painel (janela de chegada,
 * aeroporto de ponta, ordenação). LOOSE de propósito (D13): é parseado no bundle do
 * browser — um campo novo de um produtor mais novo não pode derrubar o chip (a lição
 * das facetas `.strict()`).
 */
export const FlightFilterPatchSchema = z.looseObject({
  airlines: z.array(z.string().min(2).max(3)).max(10).optional(),
  /** "sem Gol": a lista acima EXCLUI em vez de restringir. */
  airlinesExclude: z.boolean().optional(),
  stops: z.enum(['nonstop', 'up_to_1', 'up_to_2', 'any']).optional(),
  departureWindow: FlightFilterClockWindowSchema.optional(),
  arrivalWindow: FlightFilterClockWindowSchema.optional(),
  maxPrice: z.number().positive().optional(),
  /** Piso de preço ("acima de R$ 1.000", "entre 500 e 900"): o painel já tem a régua (#2110). */
  minPrice: z.number().positive().optional(),
  /** `false` = liberar também as opções fora da política. */
  withinPolicyOnly: z.boolean().optional(),
  avoidAirports: z.array(z.string().regex(IATA)).max(10).optional(),
  viaAirports: z.array(z.string().regex(IATA)).max(10).optional(),
  /** Aeroporto de PONTA da perna (saída/chegada), não conexão. */
  airports: z.array(z.string().regex(IATA)).max(10).optional(),
  airportsExclude: z.boolean().optional(),
  sort: z.enum(['rec', 'price', 'dep', 'dur', 'arr']).optional(),
  /** Cabine pedida por texto ("só executiva"). NÃO é corte: o explorer dispara o seam
   *  `cabin.onSearch` — a mesma re-busca do clique no painel (R2-D4). */
  cabin: FlightFilterPatchCabinSchema.optional(),
});
export type FlightFilterPatch = z.infer<typeof FlightFilterPatchSchema>;

export const FLIGHT_FILTER_CHIP_AXES = [
  'airline',
  'stops',
  'time',
  'period',
  'price',
  'policy',
  'via',
  'airport',
  'sort',
  'punctuality',
  'baggage',
  'seat',
  'aircraft',
  'layover',
  'duration',
  'cabin',
  'fare',
  'other',
] as const;
export const FlightFilterChipAxisSchema = z.enum(FLIGHT_FILTER_CHIP_AXES).catch('other');
export type FlightFilterChipAxis = z.infer<typeof FlightFilterChipAxisSchema>;

export const FlightFilterChipStatusSchema = z
  .enum(['applied', 'unsupported', 'unresolved'])
  .catch('unresolved');
export type FlightFilterChipStatus = z.infer<typeof FlightFilterChipStatusSchema>;

export const FLIGHT_FILTER_REASON_CODES = [
  'airport_not_in_search',
  'airline_not_in_search',
  'no_punctuality_data',
  'axis_not_available',
  'axis_not_in_panel',
  'low_confidence',
  'window_not_contiguous',
  'other',
] as const;
export const FlightFilterReasonCodeSchema = z.enum(FLIGHT_FILTER_REASON_CODES).catch('other');
export type FlightFilterReasonCode = z.infer<typeof FlightFilterReasonCodeSchema>;

export const FlightFilterChipSchema = z.looseObject({
  axis: FlightFilterChipAxisSchema,
  status: FlightFilterChipStatusSchema,
  /** Só em `applied`. */
  patch: FlightFilterPatchSchema.optional(),
  reasonCode: FlightFilterReasonCodeSchema.optional(),
  confidence: z.number().min(0).max(1).optional(),
  /** Perna que a frase nomeou ("na volta", "no segundo trecho"): índice no roteiro (ida = 0,
   *  volta = 1). Ausente = perna ativa. O explorer guarda o chip de outra perna como pendente
   *  (R2-D3). */
  leg: z.number().int().min(0).optional(),
});
export type FlightFilterChip = z.infer<typeof FlightFilterChipSchema>;

/** Chips validados UM A UM (mesmo desenho do `TolerantOffersSchema` do web-chat): um
 *  chip torto sai da lista, não derruba o intent inteiro. */
const TolerantChipsSchema = z.array(z.unknown()).transform((items) =>
  items.flatMap((item) => {
    const parsed = FlightFilterChipSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  }),
);

export const FlightFilterIntentKindSchema = z
  .enum(['filter', 'question', 'new_search', 'control', 'noise'])
  .catch('noise');
export type FlightFilterIntentKind = z.infer<typeof FlightFilterIntentKindSchema>;

export const FlightFilterControlSchema = z.enum(['reset']).optional().catch(undefined);
export type FlightFilterControl = z.infer<typeof FlightFilterControlSchema>;

/** A resposta do juiz — o que o BFF repassa e o explorer consome. */
export const FlightFilterIntentSchema = z.looseObject({
  kind: FlightFilterIntentKindSchema,
  chips: TolerantChipsSchema.default([]),
  /** Só com `kind: 'control'` — comando sobre os filtros, sem chip (R2-D6). Valor
   *  desconhecido cai fora em vez de derrubar o intent. */
  control: FlightFilterControlSchema,
  /** Telemetria: qual juiz respondeu e com que modelo. */
  judge: z.enum(['jev', 'llm']).optional(),
  model: z.string().optional(),
});
export type FlightFilterIntent = z.infer<typeof FlightFilterIntentSchema>;

/** Corpo da rota interna `POST /internal/modal-sessions/{sessionId}/filter-intent`:
 *  identidade como no refine + o texto. STRICT como os irmãos — é fio interno M2M. */
export const ModalSessionFlightFilterIntentRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    text: z.string().trim().min(1).max(300),
    locale: z.string().min(2).max(10).default('pt-BR'),
    /** Rótulos das pernas do roteiro em cena (`["ida","volta"]`, `["trecho_1",…]`): só para
     *  a pergunta `perna` do juiz. Ausente (one-way) ⇒ o juiz não pergunta perna. */
    legs: z.array(z.string().min(1).max(20)).max(6).optional(),
  })
  .strict();
export type ModalSessionFlightFilterIntentRequest = z.infer<
  typeof ModalSessionFlightFilterIntentRequestSchema
>;
